import entries from 'lodash/entries';
import get from 'lodash/get';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';

import { logCritical } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { EXPECTS } from '@savgroup-front-common/constants/shared';

import { addExternalCarrierTrackingReferenceRequest } from '../../api/Workflow/addExternalCarrierTrackingReferenceCommand';
import { explodeFileRequest } from '../../api/Workflow/explodeFileCommand';
import { forceCloseFileRequest } from '../../api/Workflow/forceCloseFileCommand';
import { getFileHandlingRequest } from '../../api/Workflow/getFileHandling';
import { getFilesForProductsRequest } from '../../api/Workflow/getFilesForProductsCommand';
import { getUserActionsRequest } from '../../api/Workflow/getUserActionsQuery';
import { refreshFileRequest } from '../../api/Workflow/refreshFileCommand';
import { setCommercialSolutionRequest } from '../../api/Workflow/setCommercialSolutionCommand';
import { setDevicePasswordRequest } from '../../api/Workflow/setDevicePasswordCommand';
import { splitFileAlongSolutionsRequest } from '../../api/Workflow/splitFileAlongSolutionsCommand';
import { splitFileRequest } from '../../api/Workflow/splitFileCommand';
import { updateFileOutcomeRequest } from '../../api/Workflow/updateFileOutcomeCommand';
import { executeSagaRequest } from '../../services';

import { noop } from './actionCreators';
import * as ActionTypes from './actionTypes';

export function* saveDevicePasswordWorker({ payload = {} }) {
  const { fileId, devicePassword } = payload;
  const response = yield executeSagaRequest(
    ActionTypes.SAVE_DEVICE_PASSWORD,
    setDevicePasswordRequest({ fileId, devicePassword }),
  );

  yield put(ActionTypes.SAVE_DEVICE_PASSWORD.end());

  return yield get(response, 'type') ===
    ActionTypes.SAVE_DEVICE_PASSWORD.SUCCEEDED;
}

export function* SetCommercialSolutionCommand({ payload = {} }) {
  const fileId = get(payload, 'fileId');

  yield executeSagaRequest(
    ActionTypes.SET_COMMERCIAL_SOLUTION,
    setCommercialSolutionRequest(payload),
    { fileId },
  );

  yield call(get(payload, 'callback', () => {}));

  yield put(ActionTypes.SET_COMMERCIAL_SOLUTION.end());
}

export function* loadFileHandlingWorker({ payload: { fileId } }) {
  const meta = { fileId, indexer: fileId };

  if (!fileId) {
    yield put(ActionTypes.LOAD_FILE_HANDLING.end());

    return;
  }

  yield executeSagaRequest(
    ActionTypes.LOAD_FILE_HANDLING,
    getFileHandlingRequest({ fileId }),
    meta,
  );
  yield put(ActionTypes.LOAD_FILE_HANDLING.end());
}
function* loadFileHandlingWatcher() {
  yield takeLatest(ActionTypes.LOAD_FILE_HANDLING.BASE, loadFileHandlingWorker);
}

export function* saveExpectsWorker({ payload = {} }) {
  const { fileId, expects } = payload;

  let errored = false;

  yield entries(expects).reduce(function* iterate(prev, [key, expect]) {
    yield prev;
    if (errored) {
      return;
    }

    switch (key) {
      case EXPECTS.EXPECT_UNLOCK_CODE_REQUESTED:
        errored = !(yield saveDevicePasswordWorker({
          payload: {
            fileId,
            devicePassword: expect,
          },
        }));
        break;
      default:
        break;
    }
  }, call(noop));

  if (errored) {
    yield put(ActionTypes.SAVE_EXPECTS.error());
  } else {
    yield put(ActionTypes.SAVE_EXPECTS.success());
  }

  yield put(ActionTypes.SAVE_EXPECTS.end());
}
function* saveExpectsWatcher() {
  yield takeEvery(ActionTypes.SAVE_EXPECTS.BASE, saveExpectsWorker);
}

function* usingMyOwnCarrierWorker({
  payload: {
    fileId,
    moduleDefinitionId,
    moduleInstanceId,
    carrierName,
    trackingNumber,
    unknownCarrier,
    handlingDirection,
    trackingUrl,
    comment,
  },
}) {
  const meta = {
    fileId,
    comment,
  };

  yield executeSagaRequest(
    ActionTypes.USE_MY_OWN_CARRIER,
    addExternalCarrierTrackingReferenceRequest({
      fileId,
      moduleDefinitionId,
      moduleInstanceId,
      carrierName,
      trackingNumber,
      unknownCarrier,
      handlingDirection,
      trackingUrl,
    }),
    meta,
  );
  yield put(ActionTypes.USE_MY_OWN_CARRIER.end(meta));
}
function* usingMyOwnCarrierWatcher() {
  yield takeEvery(ActionTypes.USE_MY_OWN_CARRIER.BASE, usingMyOwnCarrierWorker);
}

export function* forceCloseFileWorker({ payload = {} }) {
  const { fileId, closingType, comment, toCustomerComment } = payload;

  if (closingType) {
    yield executeSagaRequest(
      ActionTypes.FORCE_CLOSE_FILE,
      updateFileOutcomeRequest({ fileId, closing: closingType }),
    );
  }

  yield executeSagaRequest(
    ActionTypes.FORCE_CLOSE_FILE,
    forceCloseFileRequest({ fileId, comment, toCustomerComment }),
  );
  yield put(ActionTypes.FORCE_CLOSE_FILE.end({ fileId }));
}

function* getUserActionsWorker({ payload: fileId }) {
  const meta = { fileId };

  if (!fileId) {
    yield put(ActionTypes.GET_FILE_ACTIONS.end(meta));

    return;
  }

  yield executeSagaRequest(
    ActionTypes.GET_FILE_ACTIONS,
    getUserActionsRequest({ fileId }),
    meta,
  );
  yield put(ActionTypes.GET_FILE_ACTIONS.end(meta));
}
function* getUserActionsWatcher() {
  yield takeEvery(ActionTypes.GET_FILE_ACTIONS.BASE, getUserActionsWorker);
}

function* setSyncUserActionWorker({ payload: { fileId } }) {
  yield put(ActionTypes.SET_SYNC_USER_ACTION.success(fileId));
  yield put(ActionTypes.SET_SYNC_USER_ACTION.end(fileId));
}
function* setSyncUserActionWatcher() {
  yield takeEvery(
    ActionTypes.SET_SYNC_USER_ACTION.BASE,
    setSyncUserActionWorker,
  );
}

function* splitFileWorker({ payload: { fileId, ownerProductIds } }) {
  const meta = { fileId };

  yield executeSagaRequest(
    ActionTypes.SPLIT_FILE,
    splitFileRequest({ fileId, ownerProductIds }),
    meta,
  );
  yield put(ActionTypes.SPLIT_FILE.end(meta));
}

function* explodeFileWorker({ payload: fileId }) {
  const meta = { fileId };

  yield executeSagaRequest(
    ActionTypes.EXPLODE_FILE,
    explodeFileRequest({ fileId }),
    meta,
  );
  yield put(ActionTypes.EXPLODE_FILE.end(meta));
}

function* explodeFileAlongSolutionsWorker({ payload: fileId }) {
  const meta = { fileId };

  yield executeSagaRequest(
    ActionTypes.EXPLODE_FILE_ALONG_SOLUTIONS,
    splitFileAlongSolutionsRequest({ fileId }),
    meta,
  );
  yield put(ActionTypes.EXPLODE_FILE_ALONG_SOLUTIONS.end(meta));
}

function* forceCloseFileWatcher() {
  yield takeEvery(ActionTypes.FORCE_CLOSE_FILE.BASE, forceCloseFileWorker);
}

function* setCommercialSolutionWatcher() {
  yield takeEvery(
    ActionTypes.SET_COMMERCIAL_SOLUTION.BASE,
    SetCommercialSolutionCommand,
  );
}

function* splitFileWatcher() {
  yield takeEvery(ActionTypes.SPLIT_FILE.BASE, splitFileWorker);
}

function* explodeFileWatcher() {
  yield takeEvery(ActionTypes.EXPLODE_FILE.BASE, explodeFileWorker);
}

function* explodeFileAlongSolutionsWatcher() {
  yield takeEvery(
    ActionTypes.EXPLODE_FILE_ALONG_SOLUTIONS.BASE,
    explodeFileAlongSolutionsWorker,
  );
}

function* loadOwnerProductFilesWorker({ payload: { ownerProductIds } }) {
  const meta = { ownerProductIds };

  yield executeSagaRequest(
    ActionTypes.LOAD_OWNER_PRODUCT_FILES,
    getFilesForProductsRequest({ ownerProductIds }),
    meta,
  );
  yield put(ActionTypes.LOAD_OWNER_PRODUCT_FILES.end(meta));
}

function* loadOwnerProductFilesWatcher() {
  yield takeEvery(
    ActionTypes.LOAD_OWNER_PRODUCT_FILES.BASE,
    loadOwnerProductFilesWorker,
  );
}

function* refreshFileHandlerWorker({ payload: fileId }) {
  const meta = { fileId };

  yield executeSagaRequest(
    ActionTypes.REFRESH_FILE_HANDLER,
    refreshFileRequest({ fileId }),
    meta,
  );
  yield put(ActionTypes.REFRESH_FILE_HANDLER.end(meta));
}

function* refreshFileHandlerWatcher() {
  yield takeEvery(
    ActionTypes.REFRESH_FILE_HANDLER.BASE,
    refreshFileHandlerWorker,
  );
}

export default function* workflowSaga() {
  try {
    yield all([
      saveExpectsWatcher(),
      loadFileHandlingWatcher(),
      usingMyOwnCarrierWatcher(),
      forceCloseFileWatcher(),

      getUserActionsWatcher(),
      setSyncUserActionWatcher(),

      splitFileWatcher(),
      explodeFileWatcher(),
      explodeFileAlongSolutionsWatcher(),
      setCommercialSolutionWatcher(),
      loadOwnerProductFilesWatcher(),
      refreshFileHandlerWatcher(),
    ]);
  } catch (error) {
    logCritical(error);
  }
}
